import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Settings, Users, FileText, Play, Mail, CheckCircle,
  CreditCard, ExternalLink, Inbox, Building2, Calendar, RefreshCw,
  Info, Bell, Sheet, Plus, Trash2, Send, ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const InfoTooltip = ({ text }) => (
  <div className="relative inline-block group align-middle ml-1.5 cursor-help">
    <Info size={13} className="text-gray-400 hover:text-[#006080]" />
    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </div>
);

// ── Notifications tab ─────────────────────────────────────────────
function NotificationsTab() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});

  useEffect(() => {
    client.get("/notifications/").then(r => { setWebhooks(r.data.webhooks || []); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const add = () => setWebhooks(prev => [...prev, { name: "", type: "slack", url: "", events: ["completed", "failed"] }]);
  const remove = (i) => setWebhooks(prev => prev.filter((_, idx) => idx !== i));
  const update = (i, field, val) => setWebhooks(prev => prev.map((w, idx) => idx === i ? { ...w, [field]: val } : w));
  const toggleEvent = (i, ev) => update(i, "events", webhooks[i].events.includes(ev) ? webhooks[i].events.filter(e => e !== ev) : [...webhooks[i].events, ev]);

  const save = async () => {
    setSaving(true);
    try { await client.put("/notifications/", { webhooks }); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const test = async (i) => {
    const wh = webhooks[i];
    if (!wh.url) return;
    setTesting(t => ({ ...t, [i]: true })); setTestResult(r => ({ ...r, [i]: null }));
    try {
      const res = await client.post("/notifications/test", { type: wh.type, url: wh.url });
      setTestResult(r => ({ ...r, [i]: res.data }));
    } catch (e) {
      setTestResult(r => ({ ...r, [i]: { ok: false, http_status: null, response: e?.response?.data?.detail || String(e) } }));
    } finally { setTesting(t => ({ ...t, [i]: false })); }
  };

  const TYPE_ICONS = { slack: "S", discord: "D", generic: "W" };
  const TYPE_LABELS = { slack: "Slack", discord: "Discord", generic: "Webhook" };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-gray-700">Outbound Notifications</p>
          <InfoTooltip text="Configure Slack, Discord, or generic webhooks to receive a notification whenever an execution completes or fails. Each webhook fires a formatted payload with execution summary stats." />
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={add}>
          <Plus size={12} /> Add Webhook
        </Button>
      </div>

      {loading ? <div className="py-8 text-center text-gray-400 text-sm">Loading...</div> : webhooks.length === 0 ? (
        <div className="py-10 text-center">
          <Bell size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-400">No notification webhooks configured yet.</p>
          <p className="text-xs text-gray-300 mt-1">Click "Add Webhook" to connect Slack, Discord, or any webhook URL.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {webhooks.map((wh, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Name</Label>
                  <Input className="h-8 text-sm" value={wh.name} onChange={e => update(i, "name", e.target.value)} placeholder="e.g. Team Slack" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Type</Label>
                  <Select value={wh.type} onValueChange={v => update(i, "type", v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["slack", "discord", "generic"].map(t => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500 mb-1 block">Trigger On</Label>
                    <div className="flex gap-2 h-8 items-center">
                      {["completed", "failed"].map(ev => (
                        <label key={ev} className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox" className="w-3 h-3" checked={wh.events?.includes(ev)} onChange={() => toggleEvent(i, ev)} />
                          <span className="text-xs capitalize">{ev}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Input className="h-8 text-sm font-mono flex-1" value={wh.url} onChange={e => update(i, "url", e.target.value)} placeholder={wh.type === "slack" ? "https://hooks.slack.com/..." : wh.type === "discord" ? "https://discord.com/api/webhooks/..." : "https://your-server.com/webhook"} />
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => test(i)} disabled={testing[i] || !wh.url}>
                  <Send size={11} />{testing[i] ? "Sending..." : "Test"}
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:text-red-600" onClick={() => remove(i)}>
                  <Trash2 size={13} />
                </Button>
              </div>
              {testResult[i] && (
                <div className={`mt-2 p-2 rounded text-xs ${testResult[i].ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                  {testResult[i].ok ? `✅ Success (HTTP ${testResult[i].http_status})` : `❌ Failed: ${testResult[i].response}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {webhooks.length > 0 && (
        <Button className="mt-4 bg-[#006080] hover:bg-[#004d66] text-white" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save Webhooks"}
        </Button>
      )}
    </div>
  );
}

// ── Google Sheets tab ──────────────────────────────────────────────
function GoogleSheetsTab() {
  const [saInfo, setSaInfo] = useState(null);
  const [saJson, setSaJson] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/google-sheets/service-account").then(r => setSaInfo(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!saJson.trim()) { setMsg({ ok: false, text: "Paste your service account JSON." }); return; }
    setSaving(true); setMsg(null);
    try {
      const res = await client.put("/google-sheets/service-account", { service_account_json: saJson });
      setMsg({ ok: true, text: `Saved! Email: ${res.data.client_email}` });
      setSaInfo({ configured: true, client_email: res.data.client_email });
      setSaJson("");
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Failed to save" });
    } finally { setSaving(false); }
  };

  const del = async () => {
    setDeleting(true); setMsg(null);
    try {
      await client.delete("/google-sheets/service-account");
      setSaInfo({ configured: false });
      setMsg({ ok: true, text: "Service account removed." });
    } catch (e) {
      setMsg({ ok: false, text: "Failed to remove" });
    } finally { setDeleting(false); }
  };

  if (loading) return <div className="py-8 text-center text-gray-400 text-sm">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <p className="text-sm font-semibold text-gray-700">Google Sheets Integration</p>
        <InfoTooltip text="Connect a Google service account to import data from private Google Sheets. Public sheets work without a service account. To generate a key: Google Cloud Console → IAM → Service Accounts → Create → JSON key." />
      </div>

      {/* Current status */}
      <div className={`rounded-lg p-4 border mb-4 flex items-start gap-3 ${saInfo?.configured ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
        <ShieldCheck size={18} className={saInfo?.configured ? "text-green-600 flex-shrink-0 mt-0.5" : "text-gray-400 flex-shrink-0 mt-0.5"} />
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">{saInfo?.configured ? "Service Account Configured" : "No Service Account"}</p>
          {saInfo?.configured ? (
            <p className="text-xs text-gray-500 mt-0.5 font-mono">{saInfo.client_email}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-0.5">Public sheets work without auth. For private sheets, paste your service account JSON below.</p>
          )}
        </div>
        {saInfo?.configured && (
          <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 h-7 text-xs" onClick={del} disabled={deleting}>
            {deleting ? "..." : "Remove"}
          </Button>
        )}
      </div>

      {/* JSON input */}
      <div>
        <Label className="text-xs text-gray-500 mb-1.5 block">Service Account JSON (paste full contents of downloaded key file)</Label>
        <textarea
          className="w-full h-36 p-3 font-mono text-xs border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#006080]/20 focus:border-[#006080]"
          value={saJson}
          onChange={e => setSaJson(e.target.value)}
          placeholder={'{"type": "service_account", "project_id": "...", "private_key_id": "...", "private_key": "-----BEGIN RSA PRIVATE KEY-----\\n...", "client_email": "my-sa@project.iam.gserviceaccount.com", ...}'}
          data-testid="sa-json-input"
        />
        <p className="text-xs text-gray-400 mt-1">Your key is stored securely and never exposed in API responses or logs.</p>
      </div>
      {msg && (
        <div className={`mt-3 p-2 rounded text-xs ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
          {msg.ok ? "✅" : "❌"} {msg.text}
        </div>
      )}
      <Button className="mt-3 bg-[#006080] hover:bg-[#004d66] text-white" onClick={save} disabled={saving}>
        {saving ? "Saving..." : "Save Service Account"}
      </Button>

      <div className="mt-6 pt-4 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-500 mb-2">Usage notes</p>
        <ul className="text-xs text-gray-400 space-y-1 list-disc list-inside">
          <li>Share your Google Sheet with the service account email address above (Editor permission).</li>
          <li>Public sheets ("Anyone with the link can view") work without a service account.</li>
          <li>Use the Import button on the Uploads page to paste a Google Sheets URL.</li>
        </ul>
      </div>
    </div>
  );
}

// ── Payment Inquiries tab ─────────────────────────────────────────
function PaymentInquiriesTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    client.get("/payments/contacts")
      .then(r => setContacts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const STATUS_COLOR = { new: "bg-blue-100 text-blue-700", contacted: "bg-yellow-100 text-yellow-700", converted: "bg-green-100 text-green-700" };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-gray-700">Incoming Plan Interest</p>
          <p className="text-xs text-gray-400 mt-0.5">Contact form submissions from the pricing page (Stripe OFF)</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="h-7 text-xs gap-1"><RefreshCw size={12} />Refresh</Button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-gray-400 text-sm">Loading...</div>
      ) : contacts.length === 0 ? (
        <div className="py-10 text-center">
          <Inbox size={32} className="text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-400">No inquiries yet.</p>
          <p className="text-xs text-gray-300 mt-1">When visitors submit the contact form on the pricing page, they appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((c) => (
            <div key={c._id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900 text-sm">{c.name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${STATUS_COLOR[c.status] || STATUS_COLOR.new}`}>
                      {c.status || "new"}
                    </span>
                    <span className="text-xs font-semibold text-[#006080] bg-[#006080]/10 px-2 py-0.5 rounded-full">{c.plan_name}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                    <a href={`mailto:${c.email}`} className="hover:text-[#006080] flex items-center gap-1">
                      <Mail size={11} />{c.email}
                    </a>
                    {c.company && <span className="flex items-center gap-1"><Building2 size={11} />{c.company}</span>}
                    {c.phone && <span>{c.phone}</span>}
                  </div>
                  {c.message && (
                    <p className="text-xs text-gray-400 mt-2 italic">"{c.message}"</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-300 flex items-center gap-1">
                    <Calendar size={10} />
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                  </span>
                  <a
                    href={`mailto:${c.email}?subject=Your ${c.plan_name} Plan Request`}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors hover:opacity-90"
                    style={{ background: "#006080" }}
                    data-testid={`reply-contact-${c._id}`}
                  >
                    Reply
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function AdminSettingsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("settings"); // "settings" | "inquiries"
  const [form, setForm] = useState({
    ai_enabled: false, ai_provider: "openai", ai_model: "gpt-5.2", ai_key: "",
    max_concurrency: 10,
    smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "",
    smtp_use_tls: true, smtp_from_name: "ExcelToAPI Platform",
    stripe_enabled: false, stripe_publishable_key: "", stripe_secret_key: "",
  });
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [stats, setStats] = useState(null);
  const [inquiryCount, setInquiryCount] = useState(0);

  const PROVIDERS = [
    { value: "openai", label: "OpenAI", models: ["gpt-5.2", "gpt-5.1", "gpt-4.1", "gpt-4o"] },
    { value: "anthropic", label: "Anthropic Claude", models: ["claude-4-sonnet-20250514", "claude-sonnet-4-5-20250929"] },
  ];

  useEffect(() => {
    client.get("/admin/settings").then(r => setForm(f => ({ ...f, ...r.data }))).catch(() => {});
    client.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    // Badge count for inquiries
    client.get("/payments/contacts").then(r => setInquiryCount(r.data.filter(c => c.status === "new" || !c.status).length)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setError(""); setSuccess("");
    try {
      const payload = { ...form };
      if (payload.ai_key === "***") delete payload.ai_key;
      if (payload.smtp_password === "***") delete payload.smtp_password;
      if (payload.stripe_secret_key === "***") delete payload.stripe_secret_key;
      await client.put("/admin/settings", payload);
      setSuccess("Settings saved.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await client.post("/admin/smtp/test");
      setSuccess(res.data.message);
    } catch (e) { setError(formatApiError(e)); }
    finally { setTestingSmtp(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const currentProvider = PROVIDERS.find(p => p.value === form.ai_provider);

  const TABS = [
    { id: "settings", label: "Settings", icon: Settings },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "google_sheets", label: "Google Sheets", icon: ShieldCheck },
    { id: "inquiries", label: "Payment Inquiries", icon: Inbox, badge: inquiryCount > 0 ? inquiryCount : null },
  ];

  return (
    <div className="p-6 max-w-3xl mx-auto fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
        <p className="text-gray-500 text-sm mt-0.5">Platform configuration, payments, and AI settings</p>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[["Users", stats.users, Users], ["Uploads", stats.uploads, FileText], ["Templates", stats.templates, Settings], ["Executions", stats.executions, Play]].map(([label, value, Icon]) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-black text-[#006080]">{value}</div>
              <div className="text-xs text-gray-400 uppercase tracking-widest mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`admin-tab-${tab.id}`}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${activeTab === tab.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            <tab.icon size={14} />
            {tab.label}
            {tab.badge && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Settings tab */}
      {activeTab === "settings" && (
        <>
          {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
          {success && <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-2"><CheckCircle size={14} />{success}</div>}

          {/* SMTP */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-2 mb-4"><Mail size={16} className="text-[#006080]" /><p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">SMTP Email Settings</p></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs text-gray-500">SMTP Host</Label><Input className="mt-1" value={form.smtp_host} onChange={e => set("smtp_host", e.target.value)} placeholder="smtp.gmail.com" data-testid="smtp-host-input" /></div>
              <div><Label className="text-xs text-gray-500">Port</Label><Input className="mt-1" type="number" value={form.smtp_port} onChange={e => set("smtp_port", parseInt(e.target.value))} /></div>
              <div><Label className="text-xs text-gray-500">Username</Label><Input className="mt-1" value={form.smtp_user} onChange={e => set("smtp_user", e.target.value)} placeholder="you@gmail.com" /></div>
              <div><Label className="text-xs text-gray-500">Password</Label><Input className="mt-1" type="password" value={form.smtp_password} onChange={e => set("smtp_password", e.target.value)} placeholder="App password" /></div>
              <div><Label className="text-xs text-gray-500">From Name</Label><Input className="mt-1" value={form.smtp_from_name} onChange={e => set("smtp_from_name", e.target.value)} /></div>
              <div className="flex items-center gap-2 mt-5"><Switch checked={form.smtp_use_tls} onCheckedChange={v => set("smtp_use_tls", v)} /><Label className="text-xs text-gray-600">Use TLS (STARTTLS)</Label></div>
            </div>
            <Button variant="outline" className="mt-3 h-8 text-xs" onClick={testSmtp} disabled={testingSmtp || !form.smtp_host}>{testingSmtp ? "Sending test..." : "Send Test Email"}</Button>
          </div>

          {/* Execution */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4">Execution Settings</p>
            <div className="max-w-xs">
              <Label className="text-xs text-gray-500">Max Concurrency (platform limit)</Label>
              <Input className="mt-1 w-32" type="number" min={1} max={100} value={form.max_concurrency} onChange={e => set("max_concurrency", parseInt(e.target.value) || 10)} data-testid="max-concurrency-input" />
              <p className="text-xs text-gray-400 mt-1">Maximum simultaneous API requests per execution</p>
            </div>
          </div>

          {/* AI */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div><p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">AI Mapping Assistant</p><p className="text-xs text-gray-400 mt-0.5">AI-suggested column-to-payload mappings</p></div>
              <Switch checked={form.ai_enabled} onCheckedChange={v => set("ai_enabled", v)} data-testid="ai-enabled-toggle" />
            </div>
            {form.ai_enabled && (
              <div className="space-y-4 border-t border-gray-100 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-gray-500">Provider</Label>
                    <Select value={form.ai_provider} onValueChange={v => set("ai_provider", v)}>
                      <SelectTrigger className="mt-1" data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs text-gray-500">Model</Label>
                    <Select value={form.ai_model} onValueChange={v => set("ai_model", v)}>
                      <SelectTrigger className="mt-1" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{currentProvider?.models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs text-gray-500">API Key {form.ai_key === "***" ? <span className="text-green-600">(set)</span> : <span className="text-gray-400">(not set)</span>}</Label>
                  <Input className="mt-1 font-mono text-sm" type="password" value={form.ai_key} onChange={e => set("ai_key", e.target.value)} placeholder="sk-... (leave blank to keep current)" data-testid="ai-key-input" />
                </div>
              </div>
            )}
          </div>

          {/* Stripe */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard size={16} className="text-[#006080]" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Stripe Payments</p>
                  <p className="text-xs text-gray-400 mt-0.5">Enable Stripe checkout on the pricing page</p>
                </div>
              </div>
              <Switch checked={form.stripe_enabled} onCheckedChange={v => set("stripe_enabled", v)} data-testid="stripe-enabled-toggle" />
            </div>
            {form.stripe_enabled && (
              <div className="space-y-3 border-t border-gray-100 pt-4">
                <div>
                  <Label className="text-xs text-gray-500">Publishable Key (pk_...)</Label>
                  <Input className="mt-1 font-mono text-sm" value={form.stripe_publishable_key} onChange={e => set("stripe_publishable_key", e.target.value)} placeholder="pk_test_..." data-testid="stripe-pk-input" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Secret Key {form.stripe_secret_key === "***" ? <span className="text-green-600">(set)</span> : <span className="text-gray-400">(not set)</span>}</Label>
                  <Input className="mt-1 font-mono text-sm" type="password" value={form.stripe_secret_key} onChange={e => set("stripe_secret_key", e.target.value)} placeholder="sk_test_... (leave blank to keep current)" data-testid="stripe-sk-input" />
                </div>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate("/admin/pricing")}>
                <ExternalLink size={12} />Manage Pricing Plans
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setActiveTab("inquiries")}>
                <Inbox size={12} />View Inquiries {inquiryCount > 0 && <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">{inquiryCount}</span>}
              </Button>
            </div>
          </div>

          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={save} disabled={saving} data-testid="save-admin-settings-btn">{saving ? "Saving..." : "Save All Settings"}</Button>
        </>
      )}

      {/* Notifications tab */}
      {activeTab === "notifications" && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <NotificationsTab />
        </div>
      )}

      {/* Google Sheets tab */}
      {activeTab === "google_sheets" && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <GoogleSheetsTab />
        </div>
      )}

      {/* Payment Inquiries tab */}
      {activeTab === "inquiries" && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <PaymentInquiriesTab />
        </div>
      )}
    </div>
  );
}
